<template>
  <b-modal id="journalwin" size="xl" :dialog-class="['journal-dialog', { 'journal-expanded': expanded }]"
    hide-footer @show="load" @hidden="reset">
    <template #modal-header="{ close }">
      <h5 class="modal-title journal-title" :title="title">
        <span class="jt-prefix">Журнал: </span>{{ ctx.group }} · {{ ctx.predm }}
      </h5>
      <div class="journal-header-actions">
        <button type="button" class="journal-hdr-btn" aria-label="Настройки журнала" title="Настройки журнала: какие отметки показывать"
          @click="openSettings(false)">
          <b-icon icon="gear" aria-hidden="true"></b-icon>
        </button>
        <!-- на телефоне окно и так на весь экран — кнопка не нужна -->
        <button type="button" class="journal-hdr-btn d-none d-sm-inline-flex"
          :aria-pressed="expanded" :aria-label="expanded ? 'Свернуть окно' : 'Развернуть на весь экран'"
          :title="expanded ? 'Свернуть' : 'Развернуть'" @click="toggleExpanded">
          <b-icon :icon="expanded ? 'fullscreen-exit' : 'arrows-fullscreen'" aria-hidden="true"></b-icon>
        </button>
        <button type="button" class="close" aria-label="Закрыть" @click="close()">&times;</button>
      </div>
    </template>
    <b-alert :show="!!error" variant="danger" dismissible @dismissed="error = ''">{{ error }}</b-alert>

    <!-- строка меню, как в табличном редакторе: плоские пункты с иконками.
         На телефоне — короткие подписи (jt-long скрыт), одна строка. -->
    <div class="journal-toolbar" role="toolbar" aria-label="Действия с журналом">
      <button type="button" class="j-menu" :disabled="busy" title="Загрузить состав группы из ВятГУ" @click="refreshRoster">
        <b-icon icon="arrow-repeat" aria-hidden="true"></b-icon><span>Состав<span class="jt-long"> из</span> ВятГУ</span>
      </button>
      <button type="button" class="j-menu" :aria-pressed="String(rosterOpen)" title="Состав группы: добавить, исключить, вернуть" @click="rosterOpen = !rosterOpen">
        <b-icon icon="people" aria-hidden="true"></b-icon><span>Состав<span class="jt-long"> группы</span> ({{ activeStudents.length }})</span>
      </button>
      <span class="j-menu-sep" aria-hidden="true"></span>
      <button type="button" class="j-menu" :disabled="!rows.length || !lessons.length" title="Скачать журнал в Excel (.xlsx)" @click="exportXlsx">
        <b-icon icon="file-earmark-spreadsheet" aria-hidden="true"></b-icon><span><span class="jt-long">Экспорт в </span>Excel</span>
      </button>
      <button type="button" class="j-menu" title="Ссылка на просмотр журнала для студентов" @click="openShare">
        <b-icon icon="share" aria-hidden="true"></b-icon><span><span class="jt-long">Поделиться</span><span class="jt-short-hide">Ссылка</span></span>
      </button>
    </div>

    <div v-if="rosterOpen" class="roster-panel">
      <label for="journal-add-names" class="roster-label">Добавить студентов (по одному ФИО в строке):</label>
      <b-form-textarea id="journal-add-names" v-model="namesText" rows="3" max-rows="8" placeholder="Иванов Иван Иванович"></b-form-textarea>
      <b-button size="sm" variant="primary" class="mt-2" :disabled="busy || !namesText.trim()" @click="addStudents">Добавить</b-button>
      <ul class="roster-list">
        <li v-for="s in activeStudents" :key="s.student_key">
          <span>{{ s.name }} <small class="text-muted">{{ s.source === 'vyatgu' ? 'ВятГУ' : 'вручную' }}</small></span>
          <button type="button" class="roster-remove" :disabled="busy" :aria-label="'Убрать ' + s.name + ' из группы'" @click="removeStudent(s)">✕</button>
        </li>
      </ul>
      <template v-if="inactiveStudents.length">
        <div class="roster-sub">Не в составе</div>
        <ul class="roster-list">
          <li v-for="s in inactiveStudents" :key="s.student_key">
            <span>{{ s.name }} <small class="text-muted">{{ s.excluded ? 'исключён вручную' : 'нет в списке ВятГУ' }}</small></span>
            <button type="button" class="roster-restore" :disabled="busy" :aria-label="'Вернуть ' + s.name + ' в группу'" @click="restoreStudent(s)">Вернуть</button>
          </li>
        </ul>
      </template>
    </div>

    <div v-if="loading" class="text-center py-4"><b-spinner variant="primary"></b-spinner></div>
    <p v-else-if="!rows.length" class="text-center text-muted py-3 mb-0">
      Состав группы пуст. Загрузите его из ВятГУ или добавьте студентов вручную.
    </p>
    <p v-else-if="!lessons.length" class="text-center text-muted py-3 mb-0">
      Проведённых занятий по этому предмету у группы пока нет.
    </p>

    <div v-if="!loading && rows.length && lessons.length" class="j-body">
    <div class="j-main">
    <div class="j-cellinfo" aria-live="polite">
      <template v-if="selCell && cellDraft !== null">
        <span class="j-draft-info" :class="{ 'j-draft-bad': draftParse.error }">
          Ввод «{{ cellDraft }}» → {{ draftParse.error || cellText(draftParse.patch, true) }}
        </span>
        <span class="text-muted"> · Enter — сохранить, Esc — отмена</span>
      </template>
      <template v-else-if="selCell"><b>{{ selCell.student.name }}</b> · {{ fullDate(selCell.lesson.day) }}, {{ selCell.lesson.type }} {{ selCell.lesson.time.split('-')[0] }} — {{ cellText(selCell.value) }}</template>
      <span v-else class="text-muted">Выберите ячейку: стрелки — перемещение, отметки — кнопками на панели или с клавиатуры</span>
    </div>
    <div ref="scroller" class="journal-scroll" tabindex="0" @keydown="onKey"
      :aria-label="keyHelp">
      <table class="journal-table">
        <thead>
          <tr>
            <th class="col-name" scope="col">Студент</th>
            <th v-for="(l, c) in lessons" :key="l.day + l.time" scope="col"
              class="col-lesson" :class="{ 'j-current': isCurrent(l), 'j-hasnote': !!l.note }">
              <button type="button" class="lh-title" :title="lessonTitle(l) + ' — заметка к занятию'"
                :aria-label="'Заметка к занятию ' + lessonTitle(l)" @click="openNote(c)">
                <span class="lh-date">{{ shortDate(l.day) }}<b-icon v-if="l.note" icon="journal-text" class="lh-note-mark" aria-label="есть заметка"></b-icon></span>
                <span class="lh-meta">{{ typeAbbr(l.type) }} {{ l.time.split('-')[0] }}</span>
                <span v-if="subgroupLabel(l)" class="lh-sub" :title="'Занятие у подгруппы: ' + l.subgroups">{{ subgroupLabel(l) }}</span>
              </button>
              <div v-if="on('attendance')" class="lh-actions">
                <button type="button" class="lh-btn" :disabled="busy"
                  :title="subgroupLabel(l) ? 'Занятие у подгруппы (' + subgroupLabel(l) + '): «все Я» отметит всех студентов группы' : 'Отметить явку всем, у кого её ещё нет'"
                  @click="allPresent(c)">все Я</button>
                <button type="button" class="lh-btn" :disabled="busy || !activeStudents.length"
                  title="Перекличка: по очереди отметить явку каждого студента" @click="startRollCall(c)">перекличка</button>
              </div>
            </th>
            <template v-if="on('attendance')">
              <th class="col-sum" scope="col" title="Неявки">Н</th>
              <th class="col-sum" scope="col" title="Неявки по уважительной причине">НУ</th>
            </template>
            <th v-if="on('grades')" class="col-sum" scope="col" title="Средний балл (оценки 2–5)">Ср.</th>
            <th v-if="on('credit')" class="col-sum" scope="col" title="Зачёты из выставленных зачёт/незачёт">Зач.</th>
            <th v-if="on('points')" class="col-sum" scope="col" title="Сумма баллов">Σ</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(s, r) in rows" :key="s.student_key" :class="{ 'j-inactive': !s.active }">
            <th class="col-name" scope="row">{{ s.name }}<small v-if="!s.active" class="text-muted"> ({{ s.excluded ? 'исключён' : 'выбыл' }})</small></th>
            <td v-for="(l, c) in lessons" :key="l.day + l.time"
              class="j-cell" :class="{ 'j-selected': sel && sel.r === r && sel.c === c, 'j-current': isCurrent(l) }"
              @click="select(r, c)">
              <span v-if="cellDraft !== null && sel && sel.r === r && sel.c === c" class="j-draft">{{ cellDraft }}</span>
              <template v-else>
              <span v-if="on('attendance') && cell(s, l).attendance" class="j-att" :class="'j-att-' + cell(s, l).attendance">{{ ATT_LABEL[cell(s, l).attendance] }}</span>
              <span v-if="gradeVisible(cell(s, l).grade)" class="j-grade">{{ cell(s, l).grade }}</span>
              <span v-if="pointsEditing && sel && sel.r === r && sel.c === c" class="j-points j-points-draft">{{ pointsDraft }}</span>
              <span v-else-if="on('points') && cell(s, l).points != null" class="j-points">{{ cell(s, l).points }}</span>
              </template>
            </td>
            <template v-if="on('attendance')">
              <td class="col-sum">{{ summary(s).absent || '' }}</td>
              <td class="col-sum">{{ summary(s).excused || '' }}</td>
            </template>
            <td v-if="on('grades')" class="col-sum">{{ summary(s).avg }}</td>
            <td v-if="on('credit')" class="col-sum">{{ summary(s).zach }}</td>
            <td v-if="on('points')" class="col-sum">{{ summary(s).points }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    </div>

    <!-- панель ввода: справа от таблицы (на узком экране — под таблицей) -->
    <aside class="j-side" :class="{ 'j-side-idle': !selCell }">
    <div v-if="!selCell" class="j-side-hint">
      <b>Ввод отметок</b>
      <p>Выберите ячейку в таблице — здесь появятся кнопки.</p>
      <ul>
        <li>печатайте прямо в ячейке, <kbd>Enter</kbd> — сохранить и вниз, <kbd>Tab</kbd> — вправо, <kbd>Esc</kbd> — отмена</li>
        <li v-if="on('attendance')"><kbd>я</kbd> — был, <kbd>н</kbd> — не был, <kbd>ну</kbd> — уважительная; <kbd>Пробел</kbd> — сразу «я»</li>
        <li v-if="on('grades')"><kbd>2</kbd>–<kbd>5</kbd> — оценка</li>
        <li v-if="on('credit')"><kbd>з</kbd> — зачёт, <kbd>нз</kbd> — незачёт</li>
        <li v-if="on('points')">{{ on('grades') ? 'число от 0 до 100 (до 5 — с буквой б: «4б»)' : 'число от 0 до 100' }} — баллы</li>
        <li>можно вместе: <kbd>{{ comboExample }}</kbd></li>
        <li><kbd>Del</kbd> — очистить ячейку</li>
      </ul>
    </div>
    <div v-else class="cell-editor" role="group" :aria-label="'Отметка: ' + selCell.student.name">
      <div class="editor-title">
        <span class="ed-who"><b>{{ selCell.student.name }}</b><span class="ed-when"> · {{ shortDate(selCell.lesson.day) }} {{ typeAbbr(selCell.lesson.type) }}</span></span>
        <button type="button" class="editor-close" aria-label="Закрыть панель" @click="sel = null">✕</button>
      </div>
      <div v-if="on('attendance')" class="editor-row">
        <button v-for="a in ATTENDANCE" :key="a" type="button" class="ed-btn" :class="['ed-' + a, { 'j-on': selCell.value.attendance === a }]"
          @click="setAttendance(a)">{{ ATT_LABEL[a] }}</button>
      </div>
      <div v-if="gradeButtons.length" class="editor-row">
        <button v-for="g in gradeButtons" :key="g" type="button" class="ed-btn" :class="{ 'j-on': selCell.value.grade === g }"
          @click="setGrade(g)">{{ g }}</button>
      </div>
      <div class="editor-row">
        <template v-if="on('points')">
          <label for="journal-points" class="ed-points-label">Баллы</label>
          <input id="journal-points" ref="points" v-model="pointsDraft" class="form-control ed-points" type="number"
            inputmode="numeric" min="0" max="100" step="1" placeholder="0–100"
            @focus="pointsEditing = true" @blur="pointsEditing = false"
            @keydown.enter.prevent="savePoints(true)" @keydown.esc.stop.prevent="focusTable" @change="savePoints(false)">
        </template>
        <button type="button" class="ed-btn ed-clear" @click="clearCell">Очистить</button>
      </div>
      <div v-if="pointsError" class="ed-error">{{ pointsError }}</div>
    </div>
    </aside>
    </div>

    <!-- занятие у нескольких групп (поток): журнал ведётся по каждой группе
         отдельно — группы листами внизу, как вкладки табличного редактора -->
    <div v-if="(ctx.groups || []).length > 1" class="journal-groups" role="tablist" aria-label="Группы занятия">
      <button v-for="g in ctx.groups" :key="g" type="button" role="tab" class="jg-tab"
        :class="{ 'jg-active': g === ctx.group }" :aria-selected="String(g === ctx.group)"
        :disabled="(busy || loading) && g !== ctx.group" :title="'Журнал группы ' + g" @click="switchGroup(g)">{{ g }}</button>
    </div>

    <!-- заметка к занятию: клик по заголовку столбца -->
    <b-modal id="journalnotewin" centered :title="noteLesson ? 'Занятие ' + fullDate(noteLesson.day) : ''"
      ok-title="Сохранить" cancel-title="Отмена" :ok-disabled="busy" @ok="saveNote">
      <template v-if="noteLesson">
        <dl class="note-info">
          <div><dt>Время</dt><dd>{{ noteLesson.time }}</dd></div>
          <div><dt>Тип</dt><dd>{{ noteLesson.type || '—' }}</dd></div>
          <div><dt>Кабинет</dt><dd>{{ kabText(noteLesson.kab) || '—' }}</dd></div>
        </dl>
        <p v-if="on('attendance')" class="note-summary">
          Явка: <b class="j-att-present">Я {{ noteSummary.present }}</b> ·
          <b class="j-att-absent">Н {{ noteSummary.absent }}</b> ·
          <b class="j-att-excused">НУ {{ noteSummary.excused }}</b> ·
          не отмечено {{ noteSummary.unmarked }}
        </p>
        <b-alert :show="!!noteError" variant="danger">{{ noteError }}</b-alert>
        <label for="journal-note" class="roster-label">Заметка к занятию</label>
        <b-form-textarea id="journal-note" v-model="noteText" rows="4" max-rows="12" maxlength="5000"
          placeholder="Тема, что задано, замечания…"></b-form-textarea>
        <small class="text-muted">Та же заметка показывается в окне «Заметки» из меню занятия.</small>
      </template>
    </b-modal>

    <!-- ссылка на просмотр журнала без авторизации (для студентов) -->
    <b-modal id="journalsharewin" centered title="Поделиться ссылкой" hide-footer>
      <p class="share-text">
        По ссылке журнал <b>{{ ctx.group }} · {{ ctx.predm }}</b> открывается <b>только для просмотра</b>, без входа:
        отметки всех студентов группы и заметки к занятиям (например, домашнее задание). Изменить что-либо по ссылке нельзя.
      </p>
      <div v-if="shareBusy && !shareUrl" class="text-center py-2"><b-spinner small variant="primary"></b-spinner></div>
      <template v-if="shareUrl">
        <div class="share-link">
          <input ref="shareInput" class="form-control" type="text" readonly :value="shareUrl" aria-label="Ссылка на журнал" @focus="$event.target.select()">
          <b-button variant="primary" @click="copyShare">{{ shareCopied ? 'Скопировано' : 'Копировать' }}</b-button>
        </div>
        <div class="share-actions">
          <a :href="shareUrl" target="_blank" rel="noopener" class="btn btn-sm btn-outline-primary">Открыть</a>
          <b-button size="sm" variant="outline-primary" :disabled="shareBusy" title="Старая ссылка перестанет работать" @click="renewShare">Новая ссылка</b-button>
          <b-button size="sm" variant="outline-danger" :disabled="shareBusy" @click="deleteShare">Закрыть доступ</b-button>
        </div>
        <small class="text-muted">В ссылке видны ФИО студентов. «Новая ссылка» и «Закрыть доступ» отключают ранее выданную ссылку.</small>
      </template>
      <b-alert :show="!!shareError" variant="danger" class="mt-2 mb-0">{{ shareError }}</b-alert>
    </b-modal>

    <!-- настройки журнала предмета: какие виды отметок показывать -->
    <b-modal id="journalsetwin" centered title="Настройки журнала" ok-title="Сохранить" cancel-title="Отмена"
      :ok-disabled="busy || !setForm.length" @ok="saveSettings">
      <p v-if="setFirst" class="set-first">Журнал по этому предмету открыт впервые — выберите, что в нём отмечать.</p>
      <p class="set-subject"><b>{{ ctx.predm }}</b><br><span>{{ (ctx.groups && ctx.groups.length ? ctx.groups : [ctx.group]).join(', ') }}</span></p>
      <div class="set-list" role="group" aria-label="Виды отметок">
        <label v-for="m in MODE_LIST" :key="m.id" class="set-item">
          <input v-model="setForm" type="checkbox" :value="m.id">
          <span><b>{{ m.title }}</b><small>{{ m.hint }}</small></span>
        </label>
      </div>
      <small class="text-muted">Настройка действует для этого предмета у этих групп. Отметки скрытых видов не удаляются — они снова появятся, если включить вид обратно.</small>
      <b-alert :show="!!setError" variant="danger" class="mt-2 mb-0">{{ setError }}</b-alert>
    </b-modal>

    <!-- перекличка: по очереди каждый активный студент -->
    <b-modal id="journalrollwin" centered hide-footer
      :title="rollLesson ? 'Перекличка · ' + shortDate(rollLesson.day) + ' ' + typeAbbr(rollLesson.type) : 'Перекличка'"
      @shown="focusRoll" @hidden="rc = null">
      <div v-if="rc" ref="roll" class="roll" tabindex="-1" @keydown="onRollKey">
        <template v-if="!rc.done && rollStudent">
          <div class="roll-progress">{{ rc.i + 1 }} из {{ rollStudents.length }}</div>
          <b-progress :value="rc.i + 1" :max="rollStudents.length" height="4px" class="mb-3"></b-progress>
          <div class="roll-name">{{ rollStudent.name }}</div>
          <div class="roll-current">
            Сейчас: <b>{{ ATT_LABEL[rollValue.attendance] || 'не отмечен' }}</b><span v-if="rollValue.grade">, оценка {{ rollValue.grade }}</span>
          </div>
          <div class="roll-buttons" role="group" aria-label="Явка">
            <button v-for="a in ATTENDANCE" :key="a" type="button" class="ed-btn roll-btn"
              :class="['ed-' + a, { 'j-on': rollValue.attendance === a }]" @click="rollSet(a)">{{ ATT_LABEL[a] }}</button>
          </div>
          <div class="roll-nav">
            <button type="button" class="btn btn-outline-secondary" :disabled="rc.i === 0" @click="rollMove(-1)">← Назад</button>
            <button type="button" class="btn btn-outline-secondary" @click="rollMove(1)">{{ rc.i === rollStudents.length - 1 ? 'К итогам' : 'Вперёд →' }}</button>
            <button type="button" class="btn btn-outline-danger roll-finish" @click="$bvModal.hide('journalrollwin')">Закончить</button>
          </div>
        </template>
        <template v-else>
          <p class="roll-done">Перекличка завершена</p>
          <p class="note-summary">
            <b class="j-att-present">Я {{ rollSummary.present }}</b> ·
            <b class="j-att-absent">Н {{ rollSummary.absent }}</b> ·
            <b class="j-att-excused">НУ {{ rollSummary.excused }}</b> ·
            не отмечено {{ rollSummary.unmarked }}
          </p>
          <div class="roll-nav">
            <button type="button" class="btn btn-outline-secondary" @click="rc = { ...rc, i: rollStudents.length - 1, done: false }">← К последнему студенту</button>
            <button type="button" class="btn btn-primary roll-finish" @click="$bvModal.hide('journalrollwin')">Закрыть</button>
          </div>
        </template>
      </div>
    </b-modal>
  </b-modal>
</template>

<script>
import { utils, writeFile } from 'xlsx'

const PATH = process.env.VUE_APP_PATH

const ATTENDANCE = ['present', 'absent', 'excused']
const ATT_LABEL = { present: 'Я', absent: 'Н', excused: 'НУ' }
const GRADES = ['2', '3', '4', '5']
const CREDITS = ['зач', 'незач']
// виды отметок журнала (настройка предмета, /api/journal/settings)
const MODE_LIST = [
  { id: 'attendance', title: 'Посещаемость', hint: 'Я / Н / НУ, перекличка' },
  { id: 'grades', title: 'Оценки', hint: '2, 3, 4, 5 и средний балл' },
  { id: 'credit', title: 'Зачёт / незачёт', hint: 'зач, незач' },
  { id: 'points', title: 'Баллы', hint: 'от 0 до 100 и сумма баллов' },
]
// до первой настройки — как было раньше
const DEFAULT_MODES = ['attendance', 'grades', 'credit']
const WEEKDAYS = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб']

// режим "на весь экран" запоминается между открытиями журнала;
// localStorage может быть недоступен (приватный режим) — тогда просто не помним
function readExpanded() {
  try { return localStorage.journalExpanded === 'true' } catch (e) { return false }
}

function localToday() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export default {
  name: 'journalWin',
  props: {
    login: String,
    passwordAES: String,
    // {group, predm, day, time} — занятие, из которого открыт журнал
    ctx: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      ATTENDANCE, ATT_LABEL, MODE_LIST,
      modes: DEFAULT_MODES.slice(),
      setForm: [],
      setFirst: false,
      setError: '',
      pointsDraft: '',
      // фокус в поле баллов — набираемое число дублируется в выделенной ячейке
      pointsEditing: false,
      // набор прямо в выделенной ячейке (как в электронной таблице); null — не набираем
      cellDraft: null,
      shareUrl: '',
      shareBusy: false,
      shareError: '',
      shareCopied: false,
      pointsError: '',
      loading: false,
      busy: false,
      error: '',
      students: [],
      lessons: [],
      marks: {},
      sel: null,
      rosterOpen: false,
      namesText: '',
      expanded: readExpanded(),
      noteIdx: null,
      noteText: '',
      noteError: '',
      // перекличка: {c: индекс занятия, i: индекс студента, done}
      rc: null,
    }
  },
  watch: {
    // панель ввода: черновик баллов — значение выбранной ячейки
    sel() {
      this.cellDraft = null
      const v = this.selCell ? this.selCell.value.points : null
      this.pointsDraft = v == null ? '' : String(v)
      this.pointsError = ''
    },
  },
  computed: {
    gradeButtons() {
      return [...(this.on('grades') ? GRADES : []), ...(this.on('credit') ? CREDITS : [])]
    },
    draftParse() {
      return this.cellDraft === null ? { patch: {} } : this.parseDraft(this.cellDraft)
    },
    comboExample() {
      const parts = []
      if (this.on('attendance')) parts.push('я')
      if (this.on('grades')) parts.push('5')
      else if (this.on('credit')) parts.push('з')
      if (this.on('points')) parts.push(this.on('grades') ? '73' : '73')
      return parts.join(' ')
    },
    keyHelp() {
      const h = ['Журнал: стрелки — перемещение']
      h.push('отметку печатайте прямо в ячейке, Enter — сохранить')
      if (this.on('attendance')) h.push('я — был, н — не был, ну — уважительная, Пробел — сразу «я»')
      if (this.on('grades')) h.push('2–5 — оценка')
      if (this.on('credit')) h.push('з — зачёт, нз — незачёт')
      if (this.on('points')) h.push('число — баллы')
      h.push('Del — очистить')
      return h.join(', ')
    },
    title() {
      return `Журнал: ${this.ctx.group || ''} · ${this.ctx.predm || ''}`
    },
    activeStudents() {
      return this.students.filter(s => s.active)
    },
    inactiveStudents() {
      return this.students.filter(s => !s.active)
    },
    noteLesson() {
      return this.noteIdx === null ? null : this.lessons[this.noteIdx] || null
    },
    noteSummary() {
      return this.noteLesson ? this.columnSummary(this.noteLesson) : {}
    },
    // перекличка идёт по активным студентам в порядке таблицы
    rollStudents() {
      return this.rows.filter(s => s.active)
    },
    rollLesson() {
      return this.rc ? this.lessons[this.rc.c] || null : null
    },
    rollStudent() {
      return this.rc ? this.rollStudents[this.rc.i] || null : null
    },
    rollValue() {
      return this.rollStudent && this.rollLesson ? this.cell(this.rollStudent, this.rollLesson) : {}
    },
    rollSummary() {
      return this.rollLesson ? this.columnSummary(this.rollLesson) : {}
    },
    // выбывшие показываются, только если у них есть отметки
    rows() {
      return this.students.filter(s => s.active || Object.keys(this.marks).some(k => k.endsWith('|' + s.student_key)))
    },
    selCell() {
      if (!this.sel) return null
      const student = this.rows[this.sel.r]
      const lesson = this.lessons[this.sel.c]
      if (!student || !lesson) return null
      return { student, lesson, value: this.cell(student, lesson) }
    },
  },
  methods: {
    auth() {
      return { login: this.login, passwordAES: this.passwordAES }
    },
    toggleExpanded() {
      this.expanded = !this.expanded
      try { localStorage.journalExpanded = String(this.expanded) } catch (e) { /* не запоминаем */ }
    },
    key(day, time, studentKey) {
      return `${day}|${time}|${studentKey}`
    },
    cell(student, lesson) {
      return this.marks[this.key(lesson.day, lesson.time, student.student_key)] || {}
    },
    setMarks(list) {
      const map = {}
      list.forEach(m => { map[this.key(m.day, m.time, m.student_key)] = { attendance: m.attendance, grade: m.grade, points: m.points == null ? null : m.points } })
      this.marks = map
    },
    on(mode) {
      return this.modes.includes(mode)
    },
    // текст ячейки для строки над таблицей
    cellText(v, patch = false) {
      if (patch && !Object.keys(v).length) return 'нечего сохранять'
      const parts = []
      if (this.on('attendance') && v.attendance) parts.push({ present: 'был(а)', absent: 'не был(а)', excused: 'не был(а) по уважительной причине' }[v.attendance])
      if (this.gradeVisible(v.grade)) parts.push(CREDITS.includes(v.grade) ? v.grade : 'оценка ' + v.grade)
      if (this.on('points') && v.points != null) parts.push(v.points + ' б.')
      return parts.length ? parts.join(', ') : 'нет отметки'
    },
    gradeVisible(g) {
      if (!g) return false
      return CREDITS.includes(g) ? this.on('credit') : this.on('grades')
    },
    settingsBody() {
      return { group: this.ctx.group, groups: this.ctx.groups && this.ctx.groups.length ? this.ctx.groups : [this.ctx.group], predm: this.ctx.predm }
    },
    // Набор в ячейке -> изменения отметки. Понимает (по включённым видам):
    // я / н / ну (у) — явка; 2–5 — оценка; з / зач, нз / незач — зачёт;
    // 0–100 — баллы (при включённых оценках число до 5 — с буквой «б»: «4б»).
    // Можно вместе и слитно: «я 5», «н3», «я 73б». Не упомянутое не меняется.
    parseDraft(text) {
      const norm = String(text).toLowerCase().replace(/ё/g, 'е')
      const tokens = norm.match(/\d+б?|[а-я+]+/g) || []
      if (!tokens.length) return { patch: {}, error: '' }
      const patch = {}
      const ATT = { 'я': 'present', '+': 'present', 'н': 'absent', 'ну': 'excused', 'у': 'excused' }
      const CRED = { 'з': 'зач', 'зач': 'зач', 'нз': 'незач', 'незач': 'незач' }
      for (const t of tokens) {
        if (ATT[t]) {
          if (!this.on('attendance')) return { patch, error: 'посещаемость выключена в настройках журнала' }
          patch.attendance = ATT[t]
        } else if (CRED[t]) {
          if (!this.on('credit')) return { patch, error: 'зачёт выключен в настройках журнала' }
          patch.grade = CRED[t]
        } else if (/^\d+б?$/.test(t)) {
          const n = parseInt(t, 10)
          const asPoints = t.endsWith('б') || !this.on('grades') || n < 2 || n > 5
          if (!asPoints) patch.grade = String(n)
          else if (!this.on('points')) return { patch, error: this.on('grades') ? 'оценка — от 2 до 5' : 'баллы выключены в настройках журнала' }
          else if (n > 100) return { patch, error: 'баллы — от 0 до 100' }
          else patch.points = n
        } else if (t === 'б') {
          continue
        } else {
          return { patch, error: `не понимаю «${t}»` }
        }
      }
      return { patch, error: '' }
    },
    // символ по физической клавише: работает и в английской раскладке
    keyChar(e) {
      if (e.ctrlKey || e.metaKey || e.altKey) return ''
      if (/^[0-9]$/.test(e.key)) return e.key
      if (/^[а-яё]$/i.test(e.key)) return e.key.toLowerCase()
      const RU = {
        KeyQ: 'й', KeyW: 'ц', KeyE: 'у', KeyR: 'к', KeyT: 'е', KeyY: 'н', KeyU: 'г', KeyI: 'ш', KeyO: 'щ', KeyP: 'з',
        BracketLeft: 'х', BracketRight: 'ъ', KeyA: 'ф', KeyS: 'ы', KeyD: 'в', KeyF: 'а', KeyG: 'п', KeyH: 'р', KeyJ: 'о',
        KeyK: 'л', KeyL: 'д', Semicolon: 'ж', Quote: 'э', KeyZ: 'я', KeyX: 'ч', KeyC: 'с', KeyV: 'м', KeyB: 'и', KeyN: 'т',
        KeyM: 'ь', Comma: 'б', Period: 'ю',
      }
      if (RU[e.code]) return RU[e.code]
      if (e.key === '+') return '+'
      return ''
    },
    // сохранить набранное; false — ошибка разбора (набор остаётся)
    commitDraft() {
      if (this.cellDraft === null) return true
      const { patch, error } = this.draftParse
      if (error) return false
      if (Object.keys(patch).length && this.selCell) {
        const { student, lesson, value } = this.selCell
        this.saveCell(student, lesson, { ...value, ...patch })
      }
      this.cellDraft = null
      return true
    },
    // --- Поделиться: ссылка ?j=<token> на просмотр (publicJournal.vue) ---
    async shareRequest(url, extra = {}) {
      this.shareBusy = true
      this.shareError = ''
      this.shareCopied = false
      try {
        const data = await this.post(url, { ...this.settingsBody(), ...extra })
        return data
      } catch (e) {
        this.shareError = this.errText(e, 'Не удалось получить ссылку.')
        return null
      } finally {
        this.shareBusy = false
      }
    },
    linkFor(token) {
      return `${window.location.origin}${window.location.pathname}?j=${token}`
    },
    async openShare() {
      this.sel = null
      this.shareUrl = ''
      this.$bvModal.show('journalsharewin')
      const data = await this.shareRequest('/share')
      if (data) this.shareUrl = this.linkFor(data.token)
    },
    async renewShare() {
      const ok = await this.$bvModal.msgBoxConfirm('Создать новую ссылку? Старая ссылка перестанет открываться.',
        { title: 'Новая ссылка', okTitle: 'Создать', cancelTitle: 'Отмена', centered: true, size: 'sm' })
      if (!ok) return
      const data = await this.shareRequest('/share', { renew: true })
      if (data) this.shareUrl = this.linkFor(data.token)
    },
    async deleteShare() {
      const ok = await this.$bvModal.msgBoxConfirm('Закрыть доступ к журналу по ссылке? Выданная ссылка перестанет открываться.',
        { title: 'Закрыть доступ', okTitle: 'Закрыть доступ', okVariant: 'danger', cancelTitle: 'Отмена', centered: true, size: 'sm' })
      if (!ok) return
      if (await this.shareRequest('/share/delete')) {
        this.shareUrl = ''
        this.$bvModal.hide('journalsharewin')
        this.$root.$bvToast.toast('Доступ к журналу по ссылке закрыт.', { title: 'Журнал', variant: 'success', solid: true, autoHideDelay: 4000 })
      }
    },
    async copyShare() {
      try {
        await navigator.clipboard.writeText(this.shareUrl)
      } catch (e) {
        // нет Clipboard API (http, старый браузер) — выделяем и копируем по-старому
        this.$refs.shareInput.select()
        document.execCommand('copy')
      }
      this.shareCopied = true
    },
    openSettings(first) {
      this.sel = null
      this.setFirst = first
      this.setError = ''
      this.setForm = this.modes.slice()
      this.$bvModal.show('journalsetwin')
    },
    async saveSettings(bvEvt) {
      bvEvt.preventDefault()
      this.busy = true
      this.setError = ''
      try {
        const data = await this.post('/settings', { ...this.settingsBody(), modes: this.setForm })
        this.modes = data.modes
        this.$bvModal.hide('journalsetwin')
      } catch (e) {
        this.setError = this.errText(e, 'Настройки не сохранены.')
      } finally {
        this.busy = false
      }
    },
    reset() {
      this.modes = DEFAULT_MODES.slice()
      this.pointsError = ''
      this.students = []
      this.lessons = []
      this.marks = {}
      this.sel = null
      this.error = ''
      this.rosterOpen = false
      this.namesText = ''
      this.noteIdx = null
      this.rc = null
    },
    async post(url, body) {
      const res = await this.axios.post(PATH + '/api/journal' + url, { ...this.auth(), ...body })
      return res.data
    },
    errText(e, fallback) {
      return (e && e.response && e.response.data && e.response.data.error) || fallback
    },
    switchGroup(g) {
      if (g === this.ctx.group) return
      this.$emit('switch-group', g)
      // ctx обновит родитель — перезагружаем после этого
      this.$nextTick(() => { this.reset(); this.load() })
    },
    async load() {
      if (!this.login || !this.ctx.group) return
      this.loading = true
      this.error = ''
      try {
        const data = await this.post('/get', { ...this.settingsBody(), today: localToday() })
        this.students = data.students
        this.lessons = data.lessons
        this.setMarks(data.marks)
        // первое открытие журнала по предмету — сразу настройки
        this.modes = data.modes || DEFAULT_MODES.slice()
        if (!data.modes) this.openSettings(true)
        // первый раз — пробуем сразу подтянуть состав с сайта
        if (!this.students.length) await this.refreshRoster()
      } catch (e) {
        this.error = this.errText(e, 'Не удалось загрузить журнал.')
      } finally {
        this.loading = false
      }
    },
    async refreshRoster() {
      this.busy = true
      this.error = ''
      try {
        const data = await this.post('/roster/refresh', { group: this.ctx.group, date: this.ctx.day, time: this.ctx.time })
        this.students = data.students
      } catch (e) {
        this.error = this.errText(e, 'Не удалось получить состав группы с сайта ВятГУ.') +
          ' Состав можно добавить вручную кнопкой «Состав группы».'
      } finally {
        this.busy = false
      }
    },
    async addStudents() {
      const names = this.namesText.split('\n').map(n => n.trim()).filter(Boolean)
      this.busy = true
      this.error = ''
      try {
        const data = await this.post('/roster/add', { group: this.ctx.group, names })
        this.students = data.students
        this.namesText = ''
      } catch (e) {
        this.error = this.errText(e, 'Не удалось добавить студентов.')
      } finally {
        this.busy = false
      }
    },
    async restoreStudent(s) {
      this.busy = true
      this.error = ''
      try {
        const data = await this.post('/roster/restore', { group: this.ctx.group, student_key: s.student_key })
        this.students = data.students
      } catch (e) {
        this.error = this.errText(e, 'Не удалось вернуть студента.')
      } finally {
        this.busy = false
      }
    },
    async removeStudent(s) {
      const text = s.source === 'vyatgu'
        ? `Исключить «${s.name}» из журнала? На сайте ВятГУ он останется в группе, но обновление состава из ВятГУ его больше не вернёт. Отметки сохранятся; вернуть студента можно в разделе «Не в составе».`
        : `Убрать «${s.name}» из группы? Если у студента есть отметки, он останется в журнале с пометкой «исключён» и его можно будет вернуть.`
      const ok = await this.$bvModal.msgBoxConfirm(
        text,
        { title: 'Состав группы', okTitle: 'Убрать', cancelTitle: 'Отмена', okVariant: 'danger', centered: true, size: 'sm' }
      ).catch(() => false)
      if (!ok) return
      this.busy = true
      try {
        const data = await this.post('/roster/remove', { group: this.ctx.group, student_key: s.student_key })
        this.students = data.students
        this.sel = null
      } catch (e) {
        this.error = this.errText(e, 'Не удалось убрать студента.')
      } finally {
        this.busy = false
      }
    },
    async saveCell(student, lesson, value) {
      const k = this.key(lesson.day, lesson.time, student.student_key)
      const prev = this.marks[k]
      const next = { attendance: value.attendance || null, grade: value.grade || null, points: value.points == null ? null : value.points }
      // оптимистично: сразу показываем, при ошибке откатываем
      if (!next.attendance && !next.grade && next.points == null) this.$delete(this.marks, k)
      else this.$set(this.marks, k, next)
      try {
        await this.post('/mark', {
          group: this.ctx.group, predm: this.ctx.predm, day: lesson.day, time: lesson.time,
          student_key: student.student_key, ...next,
        })
      } catch (e) {
        if (prev) this.$set(this.marks, k, prev)
        else this.$delete(this.marks, k)
        this.error = this.errText(e, 'Отметка не сохранена.')
      }
    },
    select(r, c) {
      if (this.cellDraft !== null && !this.commitDraft()) this.cellDraft = null
      this.sel = { r, c }
      this.focusTable()
    },
    focusTable() {
      this.$nextTick(() => this.$refs.scroller && this.$refs.scroller.focus({ preventScroll: true }))
    },
    focusPoints(text) {
      if (text !== undefined) this.pointsDraft = text
      this.$nextTick(() => this.$refs.points && this.$refs.points.focus())
    },
    // Enter — сохранить и перейти к следующему студенту (ввод баллов подряд)
    savePoints(next) {
      if (!this.selCell) return
      const raw = String(this.pointsDraft).trim()
      const points = raw === '' ? null : Number(raw)
      if (points !== null && !(Number.isInteger(points) && points >= 0 && points <= 100)) {
        this.pointsError = 'Баллы — целое число от 0 до 100'
        return
      }
      this.pointsError = ''
      const { student, lesson, value } = this.selCell
      if (points !== (value.points == null ? null : value.points)) this.saveCell(student, lesson, { ...value, points })
      if (next) {
        if (this.sel.r < this.rows.length - 1) this.move(1, 0)
        this.focusTable()
      }
    },
    move(dr, dc) {
      if (!this.sel) return
      const r = Math.min(Math.max(this.sel.r + dr, 0), this.rows.length - 1)
      const c = Math.min(Math.max(this.sel.c + dc, 0), this.lessons.length - 1)
      this.sel = { r, c }
      this.$nextTick(() => {
        const el = this.$refs.scroller && this.$refs.scroller.querySelector('td.j-selected')
        if (el) el.scrollIntoView({ block: 'nearest', inline: 'nearest' })
      })
    },
    // повторное нажатие того же значения — снимает его
    setAttendance(a) {
      const { student, lesson, value } = this.selCell
      this.saveCell(student, lesson, { ...value, attendance: value.attendance === a ? null : a })
      // перекличка: после явки — к следующему студенту
      if (this.sel.r < this.rows.length - 1) this.move(1, 0)
    },
    setGrade(g) {
      const { student, lesson, value } = this.selCell
      this.saveCell(student, lesson, { ...value, grade: value.grade === g ? null : g })
    },
    // очищает только видимые виды отметок — скрытые настройкой не трогаем
    clearCell() {
      const { student, lesson, value } = this.selCell
      const next = { ...value }
      if (this.on('attendance')) next.attendance = null
      if (this.gradeVisible(value.grade)) next.grade = null
      if (this.on('points')) next.points = null
      this.pointsDraft = ''
      this.saveCell(student, lesson, next)
    },
    // e.code — физическая клавиша, поэтому работает в любой раскладке:
    // Я=Z, Н=Y, У=E, З=P
    onKey(e) {
      if (!this.selCell) return
      const moves = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] }
      // идёт набор в ячейке
      if (this.cellDraft !== null) {
        if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); this.cellDraft = null; return }
        if (e.key === 'Backspace') { e.preventDefault(); this.cellDraft = this.cellDraft.slice(0, -1); return }
        const go = e.key === 'Enter' || e.code === 'NumpadEnter' ? [1, 0] : e.key === 'Tab' ? [0, e.shiftKey ? -1 : 1] : moves[e.key]
        if (go) {
          e.preventDefault()
          if (this.commitDraft()) this.move(...go)
          return
        }
        if (e.key === ' ') { e.preventDefault(); this.cellDraft += ' '; return }
        const ch = this.keyChar(e)
        if (ch) { e.preventDefault(); if (this.cellDraft.length < 24) this.cellDraft += ch }
        return
      }
      if (moves[e.key]) { e.preventDefault(); this.move(...moves[e.key]); return }
      if (e.key === 'Tab') { e.preventDefault(); this.move(0, e.shiftKey ? -1 : 1); return }
      if (e.key === 'Enter' || e.code === 'NumpadEnter') { e.preventDefault(); this.move(1, 0); return }
      // Пробел — сразу «был» и к следующему (быстрая перекличка)
      // (именно «поставить», не переключать: повторный Пробел не снимает явку)
      if (e.code === 'Space' && this.on('attendance')) {
        e.preventDefault()
        const { student, lesson, value } = this.selCell
        if (value.attendance !== 'present') this.saveCell(student, lesson, { ...value, attendance: 'present' })
        if (this.sel.r < this.rows.length - 1) this.move(1, 0)
        return
      }
      if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); this.clearCell(); return }
      if (e.key === 'Escape') { e.stopPropagation(); this.sel = null; return }
      // любой символ отметки — начать набор в ячейке
      const ch = this.keyChar(e)
      if (ch) { e.preventDefault(); this.cellDraft = ch }
    },
    async allPresent(c) {
      const lesson = this.lessons[c]
      this.busy = true
      try {
        const data = await this.post('/mark/all-present', { group: this.ctx.group, predm: this.ctx.predm, day: lesson.day, time: lesson.time })
        this.setMarks(data.marks)
      } catch (e) {
        this.error = this.errText(e, 'Не удалось отметить явку.')
      } finally {
        this.busy = false
      }
    },
    // явка по одному занятию среди активных студентов
    columnSummary(lesson) {
      const res = { present: 0, absent: 0, excused: 0, unmarked: 0 }
      this.rows.filter(s => s.active).forEach(s => {
        const a = this.cell(s, lesson).attendance
        if (a) res[a]++
        else res.unmarked++
      })
      return res
    },
    openNote(c) {
      this.sel = null
      this.noteIdx = c
      this.noteText = this.lessons[c].note || ''
      this.noteError = ''
      this.$bvModal.show('journalnotewin')
    },
    async saveNote(bvEvt) {
      bvEvt.preventDefault()
      const lesson = this.noteLesson
      if (!lesson) return
      this.busy = true
      this.noteError = ''
      try {
        const data = await this.post('/lesson-note', { predm: this.ctx.predm, day: lesson.day, time: lesson.time, note: this.noteText })
        this.$set(lesson, 'note', data.note)
        this.$bvModal.hide('journalnotewin')
      } catch (e) {
        this.noteError = this.errText(e, 'Заметка не сохранена.')
      } finally {
        this.busy = false
      }
    },
    startRollCall(c) {
      this.sel = null
      this.rc = { c, i: 0, done: false }
      this.$bvModal.show('journalrollwin')
    },
    focusRoll() {
      this.$nextTick(() => this.$refs.roll && this.$refs.roll.focus())
    },
    // выбор явки сохраняет отметку (оценка не трогается) и сразу
    // переходит к следующему студенту; после последнего — итоги
    rollSet(a) {
      const student = this.rollStudent
      const lesson = this.rollLesson
      if (!student || !lesson) return
      const value = this.cell(student, lesson)
      this.saveCell(student, lesson, { ...value, attendance: a })
      this.rollMove(1)
    },
    rollMove(d) {
      const i = this.rc.i + d
      if (i < 0) return
      if (i >= this.rollStudents.length) this.rc = { ...this.rc, done: true }
      else this.rc = { ...this.rc, i }
      this.focusRoll()
    },
    // те же физические клавиши, что в таблице: Я=Z, Н=Y, У=E
    onRollKey(e) {
      if (!this.rc || this.rc.done) return
      const byCode = { KeyZ: 'present', Space: 'present', Enter: 'present', NumpadEnter: 'present', KeyY: 'absent', KeyE: 'excused' }
      if (byCode[e.code] && !e.target.closest('button')) { e.preventDefault(); this.rollSet(byCode[e.code]); return }
      if (e.code === 'KeyZ' || e.code === 'KeyY' || e.code === 'KeyE') { e.preventDefault(); this.rollSet(byCode[e.code]); return }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.rollMove(1) }
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.rollMove(-1) }
    },
    fullDate(day) {
      const [y, m, d] = day.split('-')
      return `${WEEKDAYS[new Date(Number(y), Number(m) - 1, Number(d)).getDay()]} ${d}.${m}.${y}`
    },
    // ВятГУ для дистанционных занятий кладёт в kab HTML-ссылку — показываем текст
    kabText(kab) {
      if (typeof kab !== 'string' || kab.indexOf('<') === -1) return kab
      try {
        return new DOMParser().parseFromString(kab, 'text/html').body.textContent.trim()
      } catch (e) {
        return kab
      }
    },
    summary(s) {
      let absent = 0, excused = 0, sum = 0, n = 0, zach = 0, zachTotal = 0, pts = 0, ptsN = 0
      this.lessons.forEach(l => {
        const v = this.cell(s, l)
        if (v.attendance === 'absent') absent++
        if (v.attendance === 'excused') excused++
        if (/^[2-5]$/.test(v.grade || '')) { sum += Number(v.grade); n++ }
        if (v.grade === 'зач') { zach++; zachTotal++ }
        if (v.grade === 'незач') zachTotal++
        if (v.points != null) { pts += v.points; ptsN++ }
      })
      return { absent, excused, avg: n ? (sum / n).toFixed(1) : '', zach: zachTotal ? `${zach}/${zachTotal}` : '', points: ptsN ? pts : '' }
    },
    isCurrent(l) {
      return l.day === this.ctx.day && l.time === this.ctx.time
    },
    shortDate(day) {
      const [y, m, d] = day.split('-')
      return `${WEEKDAYS[new Date(Number(y), Number(m) - 1, Number(d)).getDay()]} ${d}.${m}`
    },
    typeAbbr(type) {
      const t = (type || '').toLowerCase()
      if (t.startsWith('лек')) return 'Лек'
      if (t.startsWith('практ')) return 'Пр'
      if (t.startsWith('лаб')) return 'Лаб'
      return (type || '').slice(0, 3)
    },
    lessonTitle(l) {
      const sub = this.subgroupLabel(l)
      return `${l.day.split('-').reverse().join('.')} ${l.time}, ${l.type || ''}${sub ? ' (' + sub + ')' : ''}${l.kab ? ', ' + l.kab : ''}`
    },
    // "УТб-2301-02-00, 1 подгруппа" -> "1 подгр." (как в lessonContent)
    subgroupLabel(l) {
      const s = l && l.subgroups
      if (typeof s !== 'string' || !s) return ''
      const nums = [...new Set(s.match(/\d+(?= подгруппа)/g) || [])]
      return nums.length ? nums.join(', ') + ' подгр.' : ''
    },
    exportXlsx() {
      // итоговые столбцы — по включённым видам отметок
      const sums = [
        ...(this.on('attendance') ? [['Н', sm => sm.absent, 5], ['НУ', sm => sm.excused, 5]] : []),
        ...(this.on('grades') ? [['Ср. балл', sm => (sm.avg === '' ? '' : Number(sm.avg)), 9]] : []),
        ...(this.on('credit') ? [['Зачёты', sm => sm.zach, 8]] : []),
        ...(this.on('points') ? [['Сумма баллов', sm => sm.points, 12]] : []),
      ]
      const onlyPoints = this.modes.length === 1 && this.on('points')
      const head = ['ФИО', ...this.lessons.map(l => `${l.day.split('-').reverse().join('.')} ${this.typeAbbr(l.type)} ${l.time.split('-')[0]}`),
        ...sums.map(x => x[0])]
      const body = this.rows.map(s => {
        const sm = this.summary(s)
        return [
          s.name + (s.active ? '' : (s.excluded ? ' (исключён)' : ' (выбыл)')),
          ...this.lessons.map(l => {
            const v = this.cell(s, l)
            if (onlyPoints) return v.points == null ? '' : v.points
            return [
              this.on('attendance') ? ATT_LABEL[v.attendance] : null,
              this.gradeVisible(v.grade) ? v.grade : null,
              this.on('points') && v.points != null ? v.points + ' б' : null,
            ].filter(Boolean).join(' ')
          }),
          ...sums.map(x => x[1](sm)),
        ]
      })
      const ws = utils.aoa_to_sheet([head, ...body])
      ws['!cols'] = [{ wch: 34 }, ...this.lessons.map(() => ({ wch: 14 })), ...sums.map(x => ({ wch: x[2] }))]

      const lessonsWs = utils.aoa_to_sheet([
        ['Дата', 'Время', 'Тип', 'Кабинет'],
        ...this.lessons.map(l => [l.day.split('-').reverse().join('.'), l.time, l.type || '', l.kab || '']),
      ])
      lessonsWs['!cols'] = [{ wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 40 }]

      const wb = utils.book_new()
      utils.book_append_sheet(wb, ws, 'Журнал')
      utils.book_append_sheet(wb, lessonsWs, 'Занятия')
      const safe = s => String(s || '').replace(/[\\/:*?"<>|]+/g, '_').trim()
      writeFile(wb, `Журнал_${safe(this.ctx.group)}_${safe(this.ctx.predm)}_${localToday()}.xlsx`)
    },
  },
}
</script>

<style>
/* Окно на весь экран: на телефоне — всегда, на компьютере — по кнопке
   "Развернуть". Тело окна — flex-колонка, чтобы таблица занимала всю
   свободную высоту, а при открытой панели ввода сжималась. */
.journal-dialog.journal-expanded {
  margin: 0;
  max-width: none;
  width: 100vw;
  height: 100%;
}
.journal-dialog.journal-expanded .modal-content {
  height: 100vh;
  height: 100dvh;
  border-radius: 0;
}
.journal-dialog.journal-expanded .modal-body {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
}
.journal-dialog.journal-expanded .modal-body > * {
  flex: none;
}
@media (max-width: 575.98px) {
  .journal-dialog {
    margin: 0;
    max-width: 100%;
    height: 100%;
  }
  .journal-dialog .modal-content {
    height: 100vh;
    height: 100dvh;
    border-radius: 0;
  }
  .journal-dialog .modal-body {
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow-y: auto;
  }
  .journal-dialog .modal-body > * {
    flex: none;
  }
}

.journal-dialog .modal-header {
  align-items: center;
}
.journal-title {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
@media (max-width: 575.98px) {
  /* телефон: журналу — максимум места. Заголовок в одну строку без
     "Журнал:", ужатые отступы шапки и тела окна */
  .journal-dialog .modal-header {
    padding: 4px 4px 4px 12px;
  }
  .journal-title {
    font-size: 15px;
  }
  .jt-prefix {
    display: none;
  }
  .journal-dialog .modal-body {
    padding: 8px;
  }
}
.journal-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
}
.journal-header-actions .close {
  float: none;
  margin: 0;
  padding: 0;
  width: 40px;
  height: 40px;
}
.journal-hdr-btn {
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--text);
  opacity: .5;
  font-size: 1.1rem;
}
.journal-hdr-btn:hover,
.journal-hdr-btn:focus-visible {
  opacity: .9;
  background: var(--item-hover);
}
</style>

<style scoped>
.journal-groups {
  display: flex;
  align-items: stretch;
  gap: 2px;
  margin-top: -1px;
  padding: 0 4px;
  background: var(--bg-muted);
  border-top: 1px solid var(--border);
  overflow-x: auto;
  scrollbar-width: thin;
}
.jg-tab {
  flex: none;
  padding: 7px 16px 5px;
  border: 0;
  border-bottom: 3px solid transparent;
  background: transparent;
  color: var(--text-muted);
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
}
.jg-tab:hover:not(:disabled):not(.jg-active) {
  background: var(--item-hover);
  color: var(--text);
}
.jg-tab:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: -2px;
}
.jg-tab:disabled {
  opacity: .5;
  cursor: default;
}
.jg-active {
  background: var(--bg-elevated);
  color: var(--primary);
  border-bottom-color: var(--primary);
  box-shadow: 0 1px 3px rgba(0, 0, 0, .12);
  cursor: default;
}
/* строка меню: плоские пункты, подсветка при наведении, разделитель */
.journal-toolbar {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 2px 4px;
  margin-bottom: 8px;
  border: 1px solid var(--border);
  background: var(--bg-elevated);
  overflow-x: auto;
  scrollbar-width: none;
}
.j-menu {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  padding: 0 10px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: var(--text);
  font: inherit;
  font-size: 14px;
  white-space: nowrap;
  cursor: pointer;
}
.j-menu svg {
  color: var(--text-muted);
  font-size: 16px;
}
.j-menu:hover:not(:disabled) {
  background: var(--item-hover);
}
.j-menu:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: -2px;
}
.j-menu[aria-pressed="true"] {
  background: var(--item-hover);
  color: var(--primary);
}
.j-menu[aria-pressed="true"] svg {
  color: var(--primary);
}
.j-menu:disabled {
  opacity: .45;
  cursor: default;
}
.j-menu-sep {
  flex: none;
  width: 1px;
  height: 20px;
  margin: 0 4px;
  background: var(--border);
}
@media (max-width: 575.98px) {
  .journal-toolbar {
    margin-bottom: 6px;
    padding: 2px;
  }
  .j-menu {
    flex: 1 1 auto;
    justify-content: center;
    gap: 4px;
    padding: 0 6px;
    font-size: 13px;
  }
  .j-menu-sep {
    margin: 0 1px;
  }
  .jt-long {
    display: none;
  }
}
.roster-panel {
  padding: 10px;
  margin-bottom: 10px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--bg-muted);
}
.roster-label {
  font-size: 13px;
  color: var(--text-muted);
}
.roster-list {
  list-style: none;
  padding: 0;
  margin: 10px 0 0;
  max-height: 200px;
  overflow-y: auto;
}
.roster-list li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 0;
  border-bottom: 1px solid var(--border);
}
.roster-remove {
  border: 0;
  background: none;
  color: var(--danger);
  min-width: 36px;
  min-height: 36px;
}
.roster-sub {
  margin-top: 10px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-muted);
}
.roster-restore {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--bg-elevated);
  color: var(--primary);
  min-height: 36px;
  padding: 0 10px;
}

.note-info {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 20px;
  margin-bottom: 8px;
}
.note-info dt {
  font-size: 12px;
  font-weight: 400;
  color: var(--text-muted);
}
.note-info dd {
  margin: 0;
}
.note-summary {
  font-size: 14px;
}

.roll {
  outline: none;
  text-align: center;
}
.roll ::v-deep .progress-bar {
  background-color: var(--primary);
}
.roll-progress {
  font-size: 13px;
  color: var(--text-muted);
  margin-bottom: 4px;
}
.roll-name {
  font-size: 1.35rem;
  font-weight: 600;
  line-height: 1.3;
  margin-bottom: 6px;
}
.roll-current {
  font-size: 14px;
  color: var(--text-muted);
  margin-bottom: 14px;
}
.roll-buttons {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 16px;
}
.roll-btn {
  flex: 1 1 0;
  max-width: 110px;
  min-height: 56px;
  font-size: 1.2rem;
}
.roll-nav {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
}
.roll-nav .btn {
  min-height: 44px;
}
.roll-finish {
  margin-left: auto;
}
.roll-done {
  font-size: 1.2rem;
  font-weight: 600;
}

/* Табличный вид (как в просмотре по ссылке): плотная сетка 1px, серая шапка,
   закреплённые шапка и ФИО, выделение ячейки рамкой, уголок у занятия с заметкой. */
.j-cellinfo {
  padding: 5px 10px;
  min-height: 30px;
  font-size: 13px;
  border: 1px solid var(--border);
  border-bottom: 0;
  background: var(--bg-elevated);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.journal-scroll {
  overflow: auto;
  overscroll-behavior: contain;
  max-height: 60vh;
  border: 1px solid var(--border);
  outline: none;
  background: var(--bg-elevated);
}
.journal-scroll:focus-visible {
  box-shadow: inset 0 0 0 2px var(--primary);
}
.journal-table {
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  color: var(--text);
  font-variant-numeric: tabular-nums;
}
.journal-table th,
.journal-table td {
  border-right: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
  background: var(--bg-elevated);
  padding: 0 8px;
  height: 30px;
}
/* закреплённая строка заголовков */
.journal-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  height: auto;
  padding: 4px 6px;
  background: var(--bg-muted);
  vertical-align: top;
  text-align: center;
  border-bottom: 2px solid var(--border-strong, #b8cdc8);
}
/* закреплённый первый столбец */
.journal-table .col-name {
  position: sticky;
  left: 0;
  z-index: 1;
  min-width: 190px;
  max-width: 240px;
  text-align: left;
  font-weight: 500;
  border-right: 2px solid var(--border-strong, #b8cdc8);
}
.journal-table thead .col-name {
  z-index: 3;
  vertical-align: middle;
}
.col-lesson {
  min-width: 70px;
  font-weight: 600;
  position: relative;
}
.col-lesson.j-current {
  box-shadow: inset 0 -3px 0 var(--primary);
}
/* у занятия есть заметка — уголок, как у ячейки с комментарием */
.col-lesson.j-hasnote::after {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  border-style: solid;
  border-width: 0 7px 7px 0;
  border-color: transparent #f59e0b transparent transparent;
}
/* дата/тип — кнопка: открывает заметку к занятию */
.lh-title {
  display: block;
  width: 100%;
  border: 0;
  border-radius: 2px;
  background: transparent;
  color: inherit;
  font: inherit;
  padding: 1px 0;
  cursor: pointer;
}
.lh-title:hover,
.lh-title:focus-visible {
  background: var(--item-hover);
}
.lh-date {
  display: block;
  white-space: nowrap;
}
.lh-note-mark {
  margin-left: 3px;
  font-size: 12px;
  color: var(--primary);
  vertical-align: -1px;
}
.lh-sub {
  display: inline-block;
  margin-top: 1px;
  padding: 0 5px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--bg-muted);
  color: var(--text);
  font-size: 10px;
  font-weight: 400;
  white-space: nowrap;
}
.lh-meta {
  display: block;
  font-size: 11px;
  font-weight: 400;
  color: var(--text-muted);
  white-space: nowrap;
}
.lh-actions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0;
  margin-top: 3px;
  border: 1px solid var(--border);
}
.lh-btn {
  font-size: 11px;
  line-height: 1.5;
  border: 0;
  border-radius: 0;
  background: var(--bg-elevated);
  color: var(--primary);
  padding: 0 4px;
  white-space: nowrap;
}
.lh-btn + .lh-btn {
  border-top: 1px solid var(--border);
}
.lh-btn:hover:not(:disabled) {
  background: var(--item-hover);
}
.j-cell {
  min-width: 70px;
  text-align: center;
  cursor: cell;
  white-space: nowrap;
}
.j-cell.j-current {
  background: var(--table-highlight);
}
.j-cell.j-selected {
  box-shadow: inset 0 0 0 2px var(--primary);
  background: rgba(13, 148, 136, .08);
}
.j-att {
  font-weight: 600;
  margin-right: 3px;
}
.j-att-present { color: var(--success); }
.j-att-absent { color: var(--danger); }
.j-att-excused { color: var(--warning); }
.j-grade {
  font-weight: 700;
}
.col-sum {
  text-align: right;
  min-width: 46px;
  font-weight: 600;
}
.journal-table td.col-sum {
  background: var(--bg-muted);
}
.journal-table thead th.col-sum {
  text-align: center;
  vertical-align: middle;
}
tr.j-inactive .col-name,
tr.j-inactive td {
  color: var(--text-muted);
}

/* панель ввода — плоская полоса под таблицей */
.cell-editor {
  position: sticky;
  bottom: 0;
  padding: 8px 10px;
  border: 1px solid var(--border);
  border-top: 2px solid var(--primary);
  border-radius: 0;
  background: var(--bg-elevated);
  box-shadow: 0 -4px 12px var(--shadow);
  z-index: 5;
}
.editor-title {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  font-size: 13px;
}
.editor-close {
  margin-left: auto;
  border: 0;
  background: none;
  color: var(--text-muted);
  min-width: 36px;
  min-height: 32px;
}
.editor-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 4px;
}
.ed-btn {
  min-width: 46px;
  min-height: 40px;
  padding: 0 10px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: var(--bg-elevated);
  color: var(--text);
  font-weight: 600;
}
.ed-btn:hover {
  background: var(--item-hover);
}
.ed-btn.j-on {
  background: var(--primary);
  border-color: var(--primary);
  color: var(--on-primary);
}
.ed-absent.j-on { background: var(--danger); border-color: var(--danger); }
.ed-excused.j-on { background: var(--warning); border-color: var(--warning); color: var(--on-warning); }
.ed-clear {
  color: var(--danger);
}

/* таблица + панель ввода */
.j-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.j-side-hint {
  display: none;
}
/* широкое окно: панель ввода — столбец справа на всю высоту таблицы */
@media (min-width: 992px) {
  .j-body {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 250px;
    align-items: stretch;
  }
  .j-side {
    min-height: 0;
    border: 1px solid var(--border);
    border-left: 0;
    background: var(--bg);
    overflow-y: auto;
    overscroll-behavior: contain;
  }
  .j-side .cell-editor {
    position: static;
    border: 0;
    border-top: 3px solid var(--primary);
    box-shadow: none;
    background: var(--bg-elevated);
    padding: 10px;
  }
  .j-side .editor-title {
    align-items: flex-start;
    font-size: 13px;
    line-height: 1.35;
  }
  .j-side .ed-when {
    display: block;
    color: var(--text-muted);
  }
  .j-side .editor-close {
    min-height: 28px;
    margin-top: -4px;
  }
  .j-side .editor-row {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    margin-bottom: 6px;
  }
  .j-side .ed-btn {
    min-width: 0;
    padding: 0 4px;
  }
  .j-side .ed-points-label {
    grid-column: 1 / -1;
    margin: 4px 0 0;
  }
  .j-side .ed-points {
    grid-column: 1 / -1;
    width: 100%;
  }
  .j-side .ed-clear {
    grid-column: 1 / -1;
  }
  .j-side-hint {
    display: block;
    padding: 12px;
    font-size: 13px;
    color: var(--text-muted);
  }
  .j-side-hint b {
    display: block;
    color: var(--text);
    margin-bottom: 4px;
  }
  .j-side-hint ul {
    padding-left: 16px;
    margin: 0;
    display: grid;
    gap: 4px;
  }
  .j-side-hint kbd {
    font-size: 11px;
    padding: 0 4px;
    border: 1px solid var(--border);
    border-radius: 3px;
    background: var(--bg-elevated);
    color: var(--text);
  }
}

/* развёрнутое окно: таблица без фиксированного предела высоты */
.journal-dialog.journal-expanded .modal-body > .j-body {
  flex: 1 1 auto;
  min-height: 200px;
}
.journal-dialog.journal-expanded .j-main > .journal-scroll {
  flex: 1 1 auto;
  min-height: 160px;
  max-height: none;
}
@media (min-width: 992px) {
  .journal-dialog.journal-expanded .j-body {
    grid-template-rows: minmax(0, 1fr);
  }
  .journal-dialog.journal-expanded .j-main {
    min-height: 0;
  }
}
@media (max-width: 991.98px) {
  .journal-dialog.journal-expanded .j-body {
    display: flex;
    flex-direction: column;
  }
  .journal-dialog.journal-expanded .j-main {
    flex: 1 1 auto;
    min-height: 0;
  }
  .journal-dialog.journal-expanded .cell-editor {
    position: static;
  }
}
@media (max-width: 575.98px) {
  /* без фиксированного предела: вся свободная высота окна */
  .journal-dialog .modal-body > .j-body {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  .journal-dialog .j-main {
    flex: 1 1 auto;
    min-height: 0;
  }
  .journal-dialog .j-main > .journal-scroll {
    flex: 1 1 auto;
    min-height: 160px;
    max-height: none;
  }
  .cell-editor {
    position: static;
  }
  .journal-table .col-name {
    min-width: 130px;
    max-width: 150px;
    font-size: 13px;
  }
}
/* баллы в ячейке и поле ввода баллов */
.j-points {
  font-weight: 600;
  color: var(--primary);
  margin-left: 2px;
}
/* набор прямо в ячейке — с мигающим курсором */
.j-draft {
  font-weight: 600;
  color: var(--text);
}
.j-draft::after,
.j-points-draft::after {
  content: "";
  display: inline-block;
  width: 1px;
  height: 1.1em;
  margin-left: 1px;
  vertical-align: -2px;
  background: var(--primary);
  animation: j-caret 1s steps(1) infinite;
}
@keyframes j-caret {
  50% { opacity: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .j-draft::after,
  .j-points-draft::after { animation: none; }
}
.j-draft-info {
  color: var(--primary);
  font-weight: 600;
}
.j-draft-bad {
  color: var(--danger);
}
.ed-points-label {
  align-self: center;
  margin: 0 4px 0 0;
  font-size: 13px;
  color: var(--text-muted);
}
.ed-points {
  width: 96px;
  min-height: 40px;
  border-radius: 4px;
}
.ed-error {
  color: var(--danger);
  font-size: 13px;
  margin-top: 4px;
}

/* окно «Поделиться» */
.share-text {
  font-size: 14px;
}
.share-link {
  display: flex;
  gap: 6px;
  margin-bottom: 10px;
}
.share-link input {
  min-width: 0;
  font-size: 13px;
}
.share-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}
.jt-short-hide {
  display: none;
}
@media (max-width: 575.98px) {
  /* на телефоне у кнопки — иконка и короткая подпись */
  .jt-short-hide {
    display: inline;
  }
}

/* окно настроек журнала */
.set-first {
  padding: 8px 10px;
  border-radius: 8px;
  background: var(--color-current-bg);
  font-size: 14px;
}
.set-subject span {
  color: var(--primary);
  font-size: 14px;
}
.set-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 10px;
}
.set-item {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 48px;
  padding: 6px 10px;
  border: 1px solid var(--border);
  border-radius: 10px;
  cursor: pointer;
  margin: 0;
}
.set-item:hover {
  background: var(--item-hover);
}
.set-item input {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  accent-color: var(--primary);
}
.set-item span {
  display: flex;
  flex-direction: column;
}
.set-item small {
  color: var(--text-muted);
}

@media (prefers-reduced-motion: reduce) {
  .journal-scroll { scroll-behavior: auto; }
}
</style>
